import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import { Heading, TextField } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { CircleButton } from "./circle-button";

let searchDelay: NodeJS.Timeout = 0 as any;

export const TitleSearchHeading = ({
  title,
  setQuery,
  headingPadding,
  suffix,
}: {
  title: string;
  setQuery: (query: string) => void;
  headingPadding?: string;
  suffix?: React.ReactNode;
}) => {
  const [queryInput, setQueryInput] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className={twMerge(
        "relative flex flex-row items-center w-full py-1",
        headingPadding
      )}
    >
      <Heading
        size="3"
        className={twMerge(
          "absolute grow whitespace-nowrap",
          isExpanded && "hidden"
        )}
      >
        {title}
      </Heading>
      <SearchButton
        value={queryInput}
        onSearch={(value) => {
          setQueryInput(value);
          clearTimeout(searchDelay);
          searchDelay = setTimeout(() => {
            setQuery(value);
          }, 500);
        }}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
      />
      {suffix && <div className="space-x-2 ml-2">{suffix}</div>}
    </div>
  );
};

// New component for collapsible search
const SearchButton = ({
  onSearch,
  value,
  className,
  isExpanded,
  setIsExpanded,
}: {
  onSearch: (query: string) => void;
  placeholder?: string;
  value?: string;
  className?: string;
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState(value || "");
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchText(newValue);
    onSearch(newValue);
  };

  // Handle blur event - collapse if empty
  const handleBlur = () => {
    if (!searchText.trim()) {
      setIsExpanded(false);
    }
  };

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  return (
    <div className={twMerge("flex grow items-center", className)}>
      <TextField.Root
        ref={inputRef}
        value={searchText}
        onChange={handleSearchChange}
        onBlur={handleBlur}
        className={twMerge(
          "-mr-8 transition-all duration-200 ease-in-out w-full",
          isExpanded ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        placeholder={t("camera-roll.actions.search")}
        autoComplete="off"
        type="search"
      >
        <TextField.Slot>
          <MagnifyingGlassIcon className="h-4 w-4" />
        </TextField.Slot>
      </TextField.Root>

      <div
        className={twMerge(
          "transition-all duration-200 ease-in-out",
          isExpanded ? "opacity-0 pointer-events-none" : "opacity-1"
        )}
      >
        <CircleButton
          icon={(p) => <MagnifyingGlassIcon {...p} />}
          tooltip={t("camera-roll.actions.search")}
          onClick={() => setIsExpanded(true)}
        />
      </div>
    </div>
  );
};
